import { useMutation, useQueryClient } from '@tanstack/react-query';
import SharedDialog from '../../../../shared/shared-dialog';
import { useState } from 'react';
import { CalendarEventModel, ResourceCalendar } from '../../../../../types';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import {
  UserKeys,
  createResourceCalendarEvent,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { dayjsWithExtensions } from '../../../../utils/dayjs-config';
import type { Dayjs } from 'dayjs';
import {
  formatEventStartPayload,
  formatEventEndPayload,
} from '../../../../utils/calendar-events-utils';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import AvatarChip from '../../../../shared/avatar-chip';
import { calendarColors } from '../../../../utils/calendar-colors';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface ResourceCalendarAddEventDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  changeLogRoles: string[];
  selectedCalendar: ResourceCalendar;
  selectedStartDateTime: number | null;
  selectedEndDateTime: number | null;
  dateRange: {
    start: number;
    end: number;
  };
}

export default function ResourceCalendarAddEventDialog({
  open,
  handleClose,
  userEmail,
  changeLogRoles,
  selectedCalendar,
  selectedStartDateTime,
  selectedEndDateTime,
  dateRange,
}: ResourceCalendarAddEventDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [autoCompleteValue, setAutoCompleteValue] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending: isCreating } = useMutation({
    mutationFn: (data: FormValues) => {
      return createResourceCalendarEvent(
        selectedCalendar.resourceEmail,
        true,
        {
          summary: data.summary || '',
          location: data.location,
          description: data.description,
          colorId: data.colorId,
          start: formatEventStartPayload(data.start),
          end: formatEventEndPayload(data.start, data.end),
          attendees: data.invitedUsers.map((user) => {
            return {
              email: user.email,
            };
          }),
        },
        changeLogMessage
      );
    },
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Calendar event created successfully!',
      });

      queryClient.setQueriesData(
        {
          queryKey: [
            UserKeys.CalendarEvents,
            selectedCalendar.resourceEmail,
            userEmail,
            {
              start: dateRange.start,
              end: dateRange.end,
            },
          ],
        },
        (oldData: CalendarEventModel[] | undefined) => {
          if (oldData) {
            return [...oldData, data];
          } else {
            return [data];
          }
        }
      );
      setChangeLogMessage('');
      reset();
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue creating a calendar event. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.calendars.resource_calendars.create_resource_calendar_event,
        {
          module: ModuleList.modules.calendars.name,
          section: ModuleList.modules.calendars.sections.resourceCalendars,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  interface FormValues {
    summary: string;
    start: {
      dateTime: Dayjs | null;
      allDay: boolean;
    };
    end: {
      dateTime: Dayjs | null;
      allDay: boolean;
    };
    location: string;
    description: string;
    invitedUsers: {
      email: string;
      type: string;
    }[];
    colorId: string;
  }

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    formState: { isLoading },
  } = useForm<FormValues>({
    defaultValues: {
      summary: '',
      start: {
        dateTime: selectedStartDateTime
          ? dayjsWithExtensions(selectedStartDateTime)
          : null,
        allDay: false,
      },
      end: {
        dateTime: selectedEndDateTime
          ? dayjsWithExtensions(selectedEndDateTime)
          : null,
        allDay: false,
      },
      location: '',
      description: '',
      invitedUsers: [],
      colorId: '9',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'invitedUsers',
  });

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    // Validate that end date is not before start date
    if (data.start.dateTime && data.end.dateTime) {
      const startDate = dayjsWithExtensions(data.start.dateTime);
      const endDate = dayjsWithExtensions(data.end.dateTime);
      if (endDate.isBefore(startDate)) {
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'End date must be on or after the start date.',
        });
        return;
      }
    }

    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        CalendarRoles.AddResourceEvents.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const allDay = watch('start.allDay');
  const startDateTime = watch('start.dateTime');

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        reset();
      }}
      title="Create Event"
      dialogContent={
        <Stack gap={1.5} justifyContent="flex-start">
          <Stack flexDirection="row" gap={1}>
            <Controller
              name="summary"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  autoFocus
                  label="Title"
                  autoComplete="off"
                  sx={{ flexGrow: 1 }}
                />
              )}
            />
            <Controller
              name="colorId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputProps={{
                    id: 'colorId',
                  }}
                  InputLabelProps={{
                    htmlFor: 'colorId',
                  }}
                  select
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                        },
                      },
                    },
                  }}
                  label="Color"
                  sx={{ width: 80 }}
                >
                  {Object.keys(calendarColors.event).map((colorId) => (
                    <MenuItem key={colorId} value={colorId}>
                      <Box
                        sx={{
                          backgroundColor:
                            calendarColors.event[
                              colorId as keyof typeof calendarColors.event
                            ].background,
                          width: 20,
                          height: 20,
                          mx: 'auto',
                        }}
                      ></Box>
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>

          <Box>
            <Stack flexDirection="row" justifyContent="space-between">
              <Controller
                name="start.dateTime"
                control={control}
                render={({ field: { value, onChange } }) => {
                  if (allDay) {
                    return (
                      <DatePicker
                        value={value}
                        onChange={(newValue) => {
                          if (newValue) {
                            onChange(newValue);
                            setValue('end.dateTime', newValue, {
                              shouldDirty: true,
                            });
                          }
                        }}
                        label="From"
                        sx={{ minWidth: 220 }}
                      />
                    );
                  } else {
                    return (
                      <DateTimePicker
                        value={value}
                        onChange={(newValue) => {
                          if (newValue) {
                            onChange(newValue);
                            setValue(
                              'end.dateTime',
                              newValue.add(30, 'minutes'),
                              {
                                shouldDirty: true,
                              }
                            );
                          }
                        }}
                        label="From"
                        sx={{ minWidth: 220 }}
                      />
                    );
                  }
                }}
              />
              <Controller
                name="end.dateTime"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value || !startDateTime) return true;
                    const endDate = dayjsWithExtensions(value);
                    const startDate = dayjsWithExtensions(startDateTime);
                    return (
                      !endDate.isBefore(startDate) ||
                      'End date must be on or after the start date'
                    );
                  },
                }}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  if (allDay) {
                    return (
                      <DatePicker
                        value={value}
                        onChange={onChange}
                        label="To"
                        sx={{ minWidth: 220 }}
                        minDate={startDateTime ?? undefined}
                        slotProps={{
                          textField: {
                            error: !!error,
                            helperText: error?.message || ' ',
                          },
                        }}
                      />
                    );
                  }
                  return (
                    <DateTimePicker
                      value={value}
                      onChange={onChange}
                      label="To"
                      sx={{ minWidth: 220 }}
                      minDateTime={startDateTime ?? undefined}
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error?.message || ' ',
                        },
                      }}
                    />
                  );
                }}
              />
            </Stack>
            <Controller
              name="start.allDay"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                      name="calendarEvents"
                    />
                  }
                  label="All Day"
                />
              )}
            />
          </Box>
          <Stack flexDirection="row" gap={1}>
            <Box sx={{ flexGrow: 1 }}>
              <SharedAutocomplete
                sources={['users', 'groups']}
                autocompleteValue={autoCompleteValue}
                setAutoCompleteValue={setAutoCompleteValue}
                onSelection={(value, type) => {
                  if (!value) return;
                  append({
                    email: value,
                    type: type ?? '',
                  });
                  setAutoCompleteValue('');
                }}
              />
            </Box>
          </Stack>
          <Stack
            flexDirection="row"
            sx={{
              maxHeight: 80,
              overflowY: 'auto',
              flexWrap: 'wrap',
            }}
            gap={0.5}
          >
            {fields.map((field, index) => {
              return (
                <AvatarChip
                  key={field.email}
                  email={field.email}
                  type={field.type as 'Group' | 'User' | 'Domain' | 'OrgUnit'}
                  onDelete={() => remove(index)}
                />
              );
            })}
          </Stack>

          <Controller
            name="location"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Location"
                autoComplete="off"
                sx={{ minWidth: 250 }}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                autoComplete="off"
                sx={{ minWidth: 250 }}
                multiline
                rows={3}
              />
            )}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              return mutate(getValues());
            }}
            mutateLoading={isCreating}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Create Event',
        action: handleSubmit(onSubmit),
        loading: isCreating || isLoading,
        disabled: isCreating || isLoading,
      }}
    />
  );
}
